import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AccessControlProvider, useAccessControl } from './contexts/AccessControlContext'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { ErrorProvider } from './contexts/ErrorContext'
import { Layout } from './components/layout/Layout'
import { LoginPage } from './modules/auth/LoginPage'
import { VerifyEmailPage } from './modules/auth/VerifyEmailPage'
import { PlansPage } from './modules/plans/PlansPage'
import { DealershipRegistrationPage } from './modules/dealership/DealershipRegistrationPage'
import { DashboardPage } from './modules/dashboard/DashboardPage'
import { VehiclesPage } from './modules/vehicles/VehiclesPage'
import { LeadsPage, LeadsReportPage, LeadsSettingsPage, LeadsSettingsHubPage, LeadsWebhooksPage, LeadEditPage, ConversationsPage, AutoTransferRulesPage } from './modules/leads'
import { WhatsAppTemplatesPage } from './modules/whatsapp'
import { MarketplacePage, PublicationsPage, PublicationsHistoryPage } from './modules/marketplace'
import { ProposalsPage, ProposalCreatePage, ProposalEditPage, ProposalDetailsPage } from './modules/proposals'
import {
 FinancialDashboardPage,
 FinancialChargesPage,
 BenefitsPage,
 CommissionRulesPage,
 InvoicesPage,
 InvoiceDetailsPage,
 ClosuresPage,
 CommissionsPage,
 SalesPage,
 SaleDetailsPage,
 FiscalDocumentsPage
} from './modules/financial'
import { SettingsPage, ThemeSettingsPage, PaymentFormsPage, VehicleEvaluationPage, DealershipSettingsPage, OperationPipelineSettingsPage, PreparationPipelineSettingsPage, LeadSourcesSettingsPage, LeadWorkingMediaPage, OperationSettingsHub, LeadManagementHub } from './modules/settings'
import { BillingPage } from './modules/billing'
import { UsersPage } from './modules/users/UsersPage'
import { UserRegistrationPage } from './modules/users/UserRegistrationPage'
import { UserProfilePage } from './modules/users/UserProfilePage'
import { MyProfilePage } from './modules/users/MyProfilePage'
import { VehicleRegistrationPage } from './modules/vehicles/VehicleRegistrationPage'
import { VehicleDetailsPage } from './modules/vehicles/VehicleDetailsPage'
import { VehicleEditPage } from './modules/vehicles/VehicleEditPage'
import { CustomerEditPage } from './modules/customers'
import { OperationsPage, OperationPreparationVehicleDetailPage } from './modules/operation'
import { PostSalesDashboardPage, PostSalesPipelineListPage } from './modules/post-sales'
import { ProductsPage } from './modules/products'
import { MaintenanceHistoryPage } from './modules/maintenance'
import { RoleAccessPage } from './modules/access-control/RoleAccessPage'
import { LegacyRbacRoute } from './modules/access-control/LegacyRbacRoute'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { NotFoundPage } from './pages/NotFoundPage'
import {
  getLocalizedPublicPath,
  resolvePublicLocale,
  isPublicLocale,
  stripLocalePrefix,
  localeToI18nCode,
} from './lib/publicLocaleRouting'

const LEGACY_DASHBOARD_PREFIX = '/dashboard'

function LegacyDashboardRedirect() {
  const location = useLocation()
  const stripped = location.pathname.startsWith(LEGACY_DASHBOARD_PREFIX)
    ? location.pathname.slice(LEGACY_DASHBOARD_PREFIX.length)
    : ''
  const path =
    stripped === '' || stripped === '/'
      ? '/'
      : stripped.startsWith('/')
        ? stripped
        : `/${stripped}`
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />
}

type PublicLocaleI18n = {
  language: string
  changeLanguage: (language: string) => Promise<unknown> | unknown
}

export function syncPublicLocaleLanguage(i18n: PublicLocaleI18n, locale?: string): void {
  if (!isPublicLocale(locale)) return

  const targetCode = localeToI18nCode(locale)
  if (i18n.language === targetCode) return

  void i18n.changeLanguage(targetCode)
}

function PublicLocaleRoute() {
  const { locale } = useParams<{ locale?: string }>()
  const location = useLocation()
  const { i18n } = useTranslation()

  useEffect(() => {
    syncPublicLocaleLanguage(i18n, locale)
  }, [locale, i18n])

  if (!isPublicLocale(locale)) {
    const fallbackLocale = resolvePublicLocale(i18n.resolvedLanguage || i18n.language)
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const remainder = pathSegments.length > 1 ? `/${pathSegments.slice(1).join('/')}` : '/'
    const localizedPath = getLocalizedPublicPath('/', remainder, fallbackLocale)

    return <Navigate to={`${localizedPath}${location.search}${location.hash}`} replace />
  }

  return <Outlet />
}

function LegacyPublicRedirect({
  targetPath,
}: {
  targetPath: '/' | '/login' | '/register-dealership' | '/verify-email' | '/privacy'
}) {
  const location = useLocation()
  const { i18n } = useTranslation()
  const localizedPath = getLocalizedPublicPath(
    location.pathname,
    targetPath,
    i18n.resolvedLanguage || i18n.language,
  )

  return <Navigate to={`${localizedPath}${location.search}${location.hash}`} replace />
}

function App() {
 useEffect(() => {
 document.title = 'ambcar - Gestão de Concessionárias'
 
 // Meta description
 let metaDescription = document.querySelector('meta[name="description"]')
 if (!metaDescription) {
 metaDescription = document.createElement('meta')
 metaDescription.setAttribute('name', 'description')
 document.head.appendChild(metaDescription)
 }
 metaDescription.setAttribute('content', 'Sistema completo para gestão de concessionárias de automóveis')
 }, [])

 return (
 <ErrorBoundary>
 <ThemeProvider>
 <AuthProvider>
 <AccessControlProvider>
 <ErrorProvider>
 <AppContent />
 </ErrorProvider>
 </AccessControlProvider>
 </AuthProvider>
 </ThemeProvider>
 </ErrorBoundary>
 )
}

// Componente interno que tem acesso ao AuthProvider
function AppContent() {
 return (
 <Router>
 <Routes>
 {/* Rotas públicas */}
 <Route path="/:locale" element={<PublicLocaleRoute />}>
   <Route index element={<PlansPage />} />
   <Route path="plans" element={<PlansPage />} />
   {/* /onboarding was an unfinished, unlinked duplicate of the real registration flow below — kept as a redirect for old bookmarks/links */}
   <Route path="onboarding" element={<Navigate to="../register-dealership" replace />} />
   <Route path="register-dealership" element={<DealershipRegistrationPage />} />
   <Route path="login" element={<PublicRoute><LoginPage /></PublicRoute>} />
   <Route path="verify-email" element={<VerifyEmailPage />} />
   <Route path="privacy" element={<PrivacyPolicyPage />} />
 </Route>

 <Route path="/plans" element={<LegacyPublicRedirect targetPath="/" />} />
 <Route path="/onboarding" element={<LegacyPublicRedirect targetPath="/register-dealership" />} />
 <Route path="/register-dealership" element={<LegacyPublicRedirect targetPath="/register-dealership" />} />
 <Route path="/login" element={<LegacyPublicRedirect targetPath="/login" />} />
 <Route path="/verify-email" element={<LegacyPublicRedirect targetPath="/verify-email" />} />
 <Route path="/privacy" element={<LegacyPublicRedirect targetPath="/privacy" />} />

 <Route path="/dashboard/*" element={<LegacyDashboardRedirect />} />
 
 <Route
 path="/"
 element={
 <ProtectedRoute>
 <Layout />
 </ProtectedRoute>
 }
 >
 <Route index element={<DashboardPage />} />
 <Route path="vehicles" element={<VehiclesPage />} />
 <Route path="vehicles/new" element={<VehicleRegistrationPage />} />
 <Route path="vehicles/:id" element={<VehicleDetailsPage />} />
 <Route path="vehicles/edit/:id" element={<VehicleEditPage />} />
 <Route path="customers/edit/:id" element={<CustomerEditPage />} />
<Route path="leads" element={<LeadsPage />} />
  <Route path="leads/report" element={<LeadsReportPage />} />
  <Route path="leads/conversations" element={<ConversationsPage />} />
  <Route path="leads/new" element={<LeadEditPage />} />
  <Route path="leads/edit/:id" element={<LeadEditPage />} />
  <Route path="leads/settings" element={<LeadsSettingsHubPage />} />
  <Route path="leads/settings/kanban" element={<LeadsSettingsPage />} />
  <Route path="leads/settings/webhooks" element={<LeadsWebhooksPage />} />
  <Route path="leads/settings/distribution" element={<AutoTransferRulesPage />} />
  <Route path="marketplace" element={<MarketplacePage />} />
  <Route path="marketplace/integrations" element={<MarketplacePage />} />
  <Route path="marketplace/publications" element={<PublicationsPage />} />
  <Route path="marketplace/history" element={<PublicationsHistoryPage />} />
  <Route path="whatsapp/templates" element={<WhatsAppTemplatesPage />} />
 <Route path="proposals" element={<ProposalsPage />} />
 <Route path="proposals/new" element={<ProposalCreatePage />} />
 <Route path="proposals/edit/:id" element={<ProposalEditPage />} />
 <Route path="proposals/:id" element={<ProposalDetailsPage />} />
 <Route path="financial" element={<Navigate to="/financial/dashboard" replace />} />
 <Route path="financial/dashboard" element={<FinancialDashboardPage />} />
 <Route path="financial/charges" element={<FinancialChargesPage />} />
 <Route path="financial/benefits" element={<BenefitsPage />} />
 <Route path="financial/commission-rules" element={<CommissionRulesPage />} />
 <Route path="financial/invoices" element={<InvoicesPage />} />
 <Route path="financial/invoices/:id" element={<InvoiceDetailsPage />} />
 <Route path="financial/closures" element={<ClosuresPage />} />
 <Route path="financial/commissions" element={<CommissionsPage />} />
 <Route path="financial/sales" element={<SalesPage />} />
 <Route path="financial/sales/:id" element={<SaleDetailsPage />} />
 <Route
   path="financial/sales-pipeline"
   element={<Navigate to="/financial/sales?tab=pipeline" replace />}
 />
 <Route path="financial/fiscal-documents" element={<FiscalDocumentsPage />} />
 {/* Operations - Unified page with tabs */}
 <Route path="operation" element={<OperationsPage />} />
 <Route path="operation/preparation/:saleId" element={<OperationPreparationVehicleDetailPage />} />

 {/* Post-Sales */}
 <Route path="post-sales" element={<Navigate to="/post-sales/dashboard" replace />} />
 <Route path="post-sales/dashboard" element={<PostSalesDashboardPage />} />
 <Route path="post-sales/pipeline" element={<PostSalesPipelineListPage />} />
 <Route path="post-sales/pipeline/:id" element={<SaleDetailsPage />} />

 {/* Backward compatibility - old operation routes */}
 <Route path="operation/post-sale" element={<Navigate to="/operation?tab=post-sale" replace />} />
 <Route path="operation/preparation" element={<Navigate to="/operation?tab=preparation" replace />} />
 <Route path="operation/deliveries" element={<Navigate to="/operation?tab=deliveries" replace />} />
 <Route path="operation/post-receipt" element={<Navigate to="/operation?tab=receipt" replace />} />
 <Route path="operation/settings/tasks" element={<Navigate to="/settings/operation/pipeline" replace />} />
 <Route path="post-sales/settings/operation-pipeline" element={<Navigate to="/settings/operation/pipeline" replace />} />
 <Route path="sellers" element={<Navigate to="/users" replace />} />
 <Route path="products" element={<ProductsPage />} />
 <Route path="maintenance" element={<MaintenanceHistoryPage />} />
 <Route path="rbac" element={<LegacyRbacRoute />} />
 <Route path="settings/role-access" element={<RoleAccessPage />} />
<Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<UserRegistrationPage />} />
        <Route path="users/edit/me" element={<MyProfilePage />} />
        <Route path="users/edit/:id" element={<UserProfilePage />} />
        <Route path="profile" element={<MyProfilePage />} />
 <Route path="settings" element={<SettingsPage />} />
 <Route path="settings/theme" element={<ThemeSettingsPage />} />
 <Route path="settings/dealership" element={<DealershipSettingsPage />} />
 <Route path="settings/billing" element={<BillingPage />} />
 <Route path="billing" element={<Navigate to="/settings/billing" replace />} />
 <Route path="settings/payment-forms" element={<PaymentFormsPage />} />
 <Route path="settings/vehicle-evaluation" element={<VehicleEvaluationPage />} />

 {/* Operation Settings Hub */}
 <Route path="settings/operation" element={<OperationSettingsHub />} />
 <Route path="settings/operation/pipeline" element={<OperationPipelineSettingsPage />} />
 <Route path="settings/operation/preparation" element={<PreparationPipelineSettingsPage />} />

 {/* Lead Management Hub */}
 <Route path="settings/leads" element={<LeadManagementHub />} />
 <Route path="settings/leads/sources" element={<LeadSourcesSettingsPage />} />
 <Route path="settings/leads/media" element={<LeadWorkingMediaPage />} />

 {/* Backward compatibility redirects */}
 <Route path="settings/proposals-config" element={<Navigate to="/settings/payment-forms" replace />} />
 <Route path="settings/operation-pipeline" element={<Navigate to="/settings/operation/pipeline" replace />} />
 <Route path="settings/preparation-pipeline" element={<Navigate to="/settings/operation/preparation" replace />} />
 <Route path="settings/lead-sources" element={<Navigate to="/settings/leads/sources" replace />} />
 <Route path="settings/lead-working-media" element={<Navigate to="/settings/leads/media" replace />} />
 </Route>
 
 <Route path="*" element={<NotFoundPage />} />
 </Routes>
 </Router>
 )
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const { isAuthenticated, isLoading, isTokenValid, logout } = useAuth()
 const { canAccessPath, isLoading: accessLoading, isReady } = useAccessControl()
 const location = useLocation()

 const waitingAccessControl = isAuthenticated && (accessLoading || !isReady)

 if (isLoading || waitingAccessControl) {
   return (
   <div className="min-h-screen flex items-center justify-center bg-surface">
   <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
   </div>
   )
 }

 if (!isTokenValid()) {
 logout()
 return <Navigate to="/login" replace />
 }

 if (!isAuthenticated) {
 return <Navigate to="/login" replace />
 }

 if (!canAccessPath(location.pathname, location.search)) {
   return <Navigate to="/" replace />
 }

 return <>{children}</>
}

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const { isAuthenticated, isLoading, isTokenValid } = useAuth()

 if (isLoading) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-surface">
 <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
 </div>
 )
 }

 if (isAuthenticated && isTokenValid()) {
 return <Navigate to="/" replace />
 }

 return <>{children}</>
}

export default App
